/**
 * Vuex submodule for AI context groups (Context Settings tab).
 */
import { formatGlChipLabel, normalizePickerId } from '../utils/aiContextHelpers';

const flattenGlList = (items = []) =>
  (items || [])
    .filter((item) => item && item.code)
    .map((item) => {
      const id = normalizePickerId(item._id) || item.code;
      return {
        value: id,
        text: formatGlChipLabel(item),
        gl_id: id,
        code: item.code,
        name: item.name || '',
      };
    });

const state = {
  contextGroups: [],
  contextGroupsLoading: false,
  contextGroupsMeta: { total: 0, page: 0, limit: 10, pageCount: 1 },
  glPickerOptions: [],
  networkPickerOptions: [],
  pickerOptionsLoading: false,
};

const mutations = {
  SET_CONTEXT_GROUPS(state, { data, meta }) {
    state.contextGroups = data || [];
    state.contextGroupsMeta = meta || state.contextGroupsMeta;
  },
  SET_CONTEXT_GROUPS_LOADING(state, loading) {
    state.contextGroupsLoading = !!loading;
  },
  SET_GL_PICKER_OPTIONS(state, options) {
    state.glPickerOptions = options || [];
  },
  SET_NETWORK_PICKER_OPTIONS(state, options) {
    state.networkPickerOptions = options || [];
  },
  SET_PICKER_OPTIONS_LOADING(state, loading) {
    state.pickerOptionsLoading = !!loading;
  },
  CLEAR_CONTEXT_GROUPS_STATE(state) {
    state.contextGroups = [];
    state.contextGroupsLoading = false;
    state.contextGroupsMeta = { total: 0, page: 0, limit: 10, pageCount: 1 };
    state.glPickerOptions = [];
    state.networkPickerOptions = [];
    state.pickerOptionsLoading = false;
  },
};

const actions = {
  async fetchContextGroups({ commit, rootState }, {
    axios,
    page = 0,
    limit = 10,
    search = '',
    network_filter = '',
    context_filter = '',
    client = '',
  }) {
    commit('SET_CONTEXT_GROUPS_LOADING', true);
    try {
      const clientId = client || rootState.client || rootState.user?.client || '';
      const response = await axios.post('/v2/ai/context-groups/list', {
        page,
        limit,
        search,
        network_filter,
        context_filter,
        include_draft: true,
        ...(clientId ? { client: clientId } : {}),
      });
      const body = response.data || {};
      if (body.status !== 'success') {
        throw new Error(body.message || 'Failed to fetch context groups');
      }
      commit('SET_CONTEXT_GROUPS', {
        data: body.data || [],
        meta: body.meta || {},
      });
      return body;
    } catch (error) {
      console.error('[ContextGroups]: fetchContextGroups error', error);
      commit('sbError', error.response?.data?.message || error.message || 'Failed to fetch context groups', { root: true });
      throw error;
    } finally {
      commit('SET_CONTEXT_GROUPS_LOADING', false);
    }
  },

  async createContextGroup({ commit, rootState, dispatch }, { axios, payload }) {
    try {
      const clientId = rootState.client || rootState.user?.client || '';
      const response = await axios.post('/v2/ai/context-groups/create', {
        ...(clientId ? { client: clientId } : {}),
        ...payload,
      });
      const body = response.data || {};
      if (body.status !== 'success') {
        throw new Error(body.message || 'Failed to create context group');
      }
      commit('sbSuccess', 'Context group created', { root: true });
      await dispatch('fetchContextGroups', { axios, client: clientId });
      return body;
      } catch (error) {
        console.error('[ContextGroups]: createContextGroup error', error);
        const validationErrors = error.response?.data?.data?.validationErrors;
        const msg = error.response?.data?.message || error.message || 'Failed to create context group';
        commit('sbError', msg, { root: true });
        const err = new Error(msg);
        if (validationErrors) err.validationErrors = validationErrors;
        throw err;
      }
  },

  async updateContextGroup({ commit, rootState, dispatch }, { axios, id, payload }) {
    try {
      const clientId = rootState.client || rootState.user?.client || '';
      const response = await axios.post('/v2/ai/context-groups/update', {
        ...(clientId ? { client: clientId } : {}),
        id,
        ...payload,
      });
      const body = response.data || {};
      if (body.status !== 'success') {
        throw new Error(body.message || 'Failed to update context group');
      }
      commit('sbSuccess', 'Context group updated', { root: true });
      await dispatch('fetchContextGroups', { axios, client: clientId });
      return body;
    } catch (error) {
      console.error('[ContextGroups]: updateContextGroup error', error);
      const msg = error.response?.data?.message || error.message || 'Failed to update context group';
      commit('sbError', msg, { root: true });
      const err = new Error(msg);
      const validationErrors = error.response?.data?.data?.validationErrors;
      if (validationErrors) err.validationErrors = validationErrors;
      throw err;
    }
  },

  async fetchGlPickerOptions({ commit, rootState }, { axios, reporting_type = 'pl' }) {
    commit('SET_PICKER_OPTIONS_LOADING', true);
    try {
      const clientId = rootState.client || rootState.user?.client || '';
      const response = await axios.post('/v2/ai/context-groups/gls', {
        reporting_type,
        ...(clientId ? { client: clientId } : {}),
      });
      const body = response.data || {};
      if (body.status !== 'success') {
        throw new Error(body.message || 'Failed to load GL list');
      }
      const options = flattenGlList(body.data || []);
      commit('SET_GL_PICKER_OPTIONS', options);
      return options;
    } catch (error) {
      console.error('[ContextGroups]: fetchGlPickerOptions error', error);
      commit('sbError', error.response?.data?.message || error.message || 'Failed to load GL list', { root: true });
      throw error;
    } finally {
      commit('SET_PICKER_OPTIONS_LOADING', false);
    }
  },

  async fetchNetworkPickerOptions({ commit, rootState }, { axios }) {
    commit('SET_PICKER_OPTIONS_LOADING', true);
    try {
      const clientId = rootState.client || rootState.user?.client || '';
      const response = await axios.post('/v2/ai/context-groups/networks', {
        ...(clientId ? { client: clientId } : {}),
      });
      const body = response.data || {};
      if (body.status !== 'success') {
        throw new Error(body.message || 'Failed to load networks');
      }
      const rows = Array.isArray(body.data) ? body.data : [];
      const options = rows.map((n) => {
        const id = normalizePickerId(n._id);
        return {
          value: id,
          text: n.name || n.network || id,
          network_id: id,
          name: n.name || n.network || '',
        };
      });
      commit('SET_NETWORK_PICKER_OPTIONS', options);
      return options;
    } catch (error) {
      console.error('[ContextGroups]: fetchNetworkPickerOptions error', error);
      commit('sbError', error.response?.data?.message || error.message || 'Failed to load networks', { root: true });
      throw error;
    } finally {
      commit('SET_PICKER_OPTIONS_LOADING', false);
    }
  },
};

const getters = {
  contextGroups: (state) => state.contextGroups,
  contextGroupsLoading: (state) => state.contextGroupsLoading,
  contextGroupsMeta: (state) => state.contextGroupsMeta,
  glPickerOptions: (state) => state.glPickerOptions,
  networkPickerOptions: (state) => state.networkPickerOptions,
  pickerOptionsLoading: (state) => state.pickerOptionsLoading,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
